import React, { Suspense, lazy } from 'react';
import PropTypes from 'prop-types';
import { LuLoaderCircle, LuMail, LuMessagesSquare } from 'react-icons/lu';
import Button from '../../../components/ui/Button';
import GlassIcon from '../../../components/ui/GlassIcon';
import { useTranslation } from '../../../i18n/LanguageContext';
import { CARD } from '../components/ProfileUi';

const Messenger = lazy(() => import('../../../components/Messenger'));

/**
 * "Mesajlar": the messenger loads on demand (it pulls every conversation), or
 * right away when the page was opened from a message notification.
 */
const MessagesPanel = ({ users, loaded, onLoad, autoOpened, requestedChatEmail, onInitialChatOpened, unreadCount }) => {
  const { t } = useTranslation();
  const label = (key, vars) => t(`profilePage.messages.${key}`, vars);

  if (!loaded) {
    return (
      <div className={`${CARD} tw-flex tw-flex-col tw-items-center tw-gap-4 tw-px-6 tw-py-12 tw-text-center`}>
        <GlassIcon gradient="tw-from-teal-400 tw-to-cyan-600 tw-shadow-teal-500/30" size="md">
          <LuMessagesSquare aria-hidden="true" className="tw-h-6 tw-w-6" />
        </GlassIcon>
        <div className="tw-space-y-1">
          <h2 className="tw-text-lg tw-font-semibold tw-text-slate-900 dark:tw-text-white">{label('title')}</h2>
          <p className="tw-mx-auto tw-max-w-md tw-text-sm tw-text-slate-500 dark:tw-text-slate-400">{label('description')}</p>
        </div>
        {unreadCount > 0 && (
          <span className="tw-inline-flex tw-items-center tw-gap-2 tw-rounded-full tw-bg-rose-500/10 tw-px-3 tw-py-1.5 tw-text-sm tw-font-semibold tw-text-rose-700 dark:tw-text-rose-300">
            <LuMail aria-hidden="true" className="tw-h-4 tw-w-4" />
            {label('unread', { count: unreadCount })}
          </span>
        )}
        <Button variant="primary" onClick={onLoad}>
          <LuMessagesSquare aria-hidden="true" className="tw-h-4 tw-w-4" />
          {label('load')}
        </Button>
      </div>
    );
  }

  return (
    <Suspense
      fallback={(
        <div className={`${CARD} tw-flex tw-items-center tw-justify-center tw-gap-2 tw-py-16 tw-text-sm tw-text-slate-500 dark:tw-text-slate-400`}>
          <LuLoaderCircle aria-hidden="true" className="tw-h-5 tw-w-5 tw-animate-spin" />
          {label('loading')}
        </div>
      )}
    >
      <Messenger
        users={users}
        deferLoad={!autoOpened}
        initialChatEmail={requestedChatEmail}
        onInitialChatOpened={onInitialChatOpened}
      />
    </Suspense>
  );
};

MessagesPanel.propTypes = {
  users: PropTypes.arrayOf(PropTypes.object).isRequired,
  loaded: PropTypes.bool.isRequired,
  onLoad: PropTypes.func.isRequired,
  autoOpened: PropTypes.bool,
  requestedChatEmail: PropTypes.string,
  onInitialChatOpened: PropTypes.func.isRequired,
  unreadCount: PropTypes.number,
};

export default MessagesPanel;
